import { isValidElement, useEffect, type ReactNode, type ComponentType } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Lock, X } from "lucide-react"

function renderIcon(icon: any, className?: string) {
  if (!icon) return null
  if (isValidElement(icon)) return icon
  const IconComponent = icon
  return <IconComponent className={className} />
}

export interface ModalAction {
  label: string
  onClick?: () => void
  href?: string
  className?: string
  variant?: "primary" | "secondary" | "outline" | "ghost" | "danger"
  disabled?: boolean
}

export interface DetailItem {
  label: string
  value: ReactNode
  icon?: ComponentType<{ className?: string }> | ReactNode
}

export interface PromptModalCardProps {
  title?: ReactNode
  description?: ReactNode
  icon?: ComponentType<{ className?: string }> | ReactNode
  iconContainerClassName?: string
  iconClassName?: string

  // Details grid / list
  details?: DetailItem[]

  // Custom content body
  children?: ReactNode

  // Actions
  primaryAction?: ModalAction
  secondaryAction?: ModalAction

  // Cancel / Close
  onCancel?: () => void
  onClose?: () => void
  onCancelText?: string | null
  onCloseText?: string | null
  showFooterLink?: boolean

  // Auth flow props (for backward compatibility)
  showAuthButtons?: boolean
  loginRedirectTo?: string
  signupRedirectTo?: string

  // Modal styling & sizing
  maxWidth?: string // default "max-w-md"
  className?: string
}

export function PromptModalCard({
  title = "Log in to request a quote",
  description = "You can keep browsing the supplier and product catalog for free. Create a free account to send quote requests and get matched with suppliers.",
  icon: Icon = Lock,
  iconContainerClassName = "bg-[#be001e]/10 text-[#be001e]",
  iconClassName = "size-6",
  details,
  children,
  primaryAction,
  secondaryAction,
  onCancel,
  onClose,
  onCancelText,
  onCloseText,
  showFooterLink,
  showAuthButtons,
  loginRedirectTo = "/login",
  signupRedirectTo = "/signup",
  maxWidth = "max-w-md",
  className = "",
}: PromptModalCardProps) {
  const navigate = useNavigate()

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else if (onCancel) {
      onCancel()
    } else {
      navigate({ to: "/" })
    }
  }

  // Prevent background scrolling while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [])

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose, onCancel])

  // Show default auth buttons if explicitly requested or if no custom actions are provided and auth URLs are set
  const renderAuthButtons =
    showAuthButtons ?? (!primaryAction && !secondaryAction && Boolean(loginRedirectTo || signupRedirectTo))

  let cancelLabel: string | null = null
  if (
    showFooterLink === false ||
    onCloseText === "" ||
    onCancelText === "" ||
    onCloseText === null ||
    onCancelText === null
  ) {
    cancelLabel = null
  } else if (onCloseText) {
    cancelLabel = onCloseText
  } else if (onCancelText) {
    cancelLabel = onCancelText
  } else if (renderAuthButtons) {
    cancelLabel = "Keep browsing"
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto overscroll-y-contain"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose()
        }
      }}
    >
      {/* Full-bleed persistent backdrop: always covers 100% of screen without keyboard closing lag */}
      <div
        className="fixed -inset-10 bg-black/40 backdrop-blur-xs animate-in fade-in duration-200 pointer-events-none"
        aria-hidden="true"
      />

      <div
        className="flex min-h-full items-center justify-center p-4 pb-28 sm:pb-6 text-center"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose()
          }
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          className={`relative ${maxWidth} w-full my-auto bg-white shadow-2xl rounded-2xl p-5 sm:p-6 border border-slate-100 text-center space-y-4 animate-in zoom-in-95 duration-200 ${className}`}
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            title="Close"
          >
            <X className="size-4" />
          </button>

          {/* Icon */}
          {Icon && (
            <div className={`size-12 rounded-full flex items-center justify-center mx-auto ${iconContainerClassName}`}>
            {renderIcon(Icon, iconClassName)}
          </div>
        )}

        {/* Title */}
        {title && <h3 className="text-base font-bold text-slate-800">{title}</h3>}

        {/* Description */}
        {description && (
          <p className="text-[13px] text-slate-500 leading-relaxed max-w-sm mx-auto">
            {description}
          </p>
        )}

        {/* Details Grid */}
        {details && details.length > 0 && (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 text-left space-y-2 my-3">
            {details.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-slate-200/60 last:border-b-0">
                <span className="text-slate-500 font-medium flex items-center gap-1.5">
                  {item.icon && renderIcon(item.icon, "size-3.5 text-slate-400")}
                  {item.label}
                </span>
                <span className="text-slate-800 font-semibold">{item.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Custom Body / Children */}
        {children && <div className="text-left py-1">{children}</div>}

        {/* Action Buttons */}
        {(primaryAction || secondaryAction || renderAuthButtons) && (
          <div className="flex flex-col sm:flex-row gap-2.5 justify-center pt-2">
            {renderAuthButtons ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate({ to: loginRedirectTo as any })}
                  className="flex-1 rounded-xl bg-[#be001e] hover:bg-[#a0001a] text-white text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer shadow-sm"
                >
                  Log In
                </button>
                <button
                  type="button"
                  onClick={() => navigate({ to: signupRedirectTo as any })}
                  className="flex-1 rounded-xl border border-[#be001e]/25 text-[#be001e] hover:bg-[#be001e]/5 text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer"
                >
                  Create Account
                </button>
              </>
            ) : (
              <>
                {primaryAction && (
                  <button
                    type="button"
                    disabled={primaryAction.disabled}
                    onClick={() => {
                      if (primaryAction.href) navigate({ to: primaryAction.href as any })
                      else primaryAction.onClick?.()
                    }}
                    className={
                      primaryAction.className ||
                      "flex-1 rounded-xl bg-[#be001e] hover:bg-[#a0001a] text-white text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer shadow-sm disabled:opacity-50"
                    }
                  >
                    {primaryAction.label}
                  </button>
                )}
                {secondaryAction && (
                  <button
                    type="button"
                    disabled={secondaryAction.disabled}
                    onClick={() => {
                      if (secondaryAction.href) navigate({ to: secondaryAction.href as any })
                      else secondaryAction.onClick?.()
                    }}
                    className={
                      secondaryAction.className ||
                      "flex-1 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px] font-bold py-2.5 transition active:scale-95 cursor-pointer disabled:opacity-50"
                    }
                  >
                    {secondaryAction.label}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Footer cancel / close link */}
        {cancelLabel && (
          <button
            type="button"
            onClick={handleClose}
            className="text-[12px] text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors cursor-pointer block mx-auto pt-1"
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </div>
  </div>
)
}

// Backward compatibility alias
export const AuthPromptCard = PromptModalCard
export type AuthPromptCardProps = PromptModalCardProps
